/**
 * Detects the average brightness of a background image to determine
 * whether white or black text should be used for optimal legibility.
 * 
 * @param imageElement - The loaded HTMLImageElement to analyze
 * @returns Object with:
 *   - averageBrightness: average brightness value (0-255)
 *   - isLight: true if background is light (brightness > 128)
 *   - isDark: true if background is dark (brightness <= 128)
 *   - useWhiteText: true if white text should be used (dark background)
 *   - useBlackText: true if black text should be used (light background)
 */
export function detectBackgroundBrightness(imageElement: HTMLImageElement): {
  averageBrightness: number;
  isLight: boolean;
  isDark: boolean;
  useWhiteText: boolean;
  useBlackText: boolean;
} {
  if (!imageElement || !imageElement.complete || imageElement.naturalWidth === 0) {
    // Image not loaded yet or invalid - default to dark background (use white text)
    return {
      averageBrightness: 100,
      isLight: false,
      isDark: true,
      useWhiteText: true,
      useBlackText: false,
    };
  }

  try {
    // Create a canvas to analyze the image
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      return {
        averageBrightness: 100,
        isLight: false,
        isDark: true,
        useWhiteText: true,
        useBlackText: false,
      };
    }

    const width = imageElement.naturalWidth;
    const height = imageElement.naturalHeight;
    
    canvas.width = width;
    canvas.height = height;
    
    // Draw the image to the canvas
    ctx.drawImage(imageElement, 0, 0);
    
    // Get image data - this can fail with CORS errors on cross-origin images
    let imageData: ImageData;
    try {
      imageData = ctx.getImageData(0, 0, width, height);
    } catch (error) {
      // CORS error - image is tainted and can't be read
      // Default to dark background (use white text)
      return {
        averageBrightness: 100,
        isLight: false,
        isDark: true,
        useWhiteText: true,
        useBlackText: false,
      };
    }
    
    const data = imageData.data;
    
    // Analyze pixels - focus on the area where text will be (top portion)
    // Sample from the top 40% of the image where the header text appears
    const textAreaHeight = Math.floor(height * 0.4);
    let totalBrightness = 0;
    let pixelCount = 0;
    
    // Sample pixels (every Nth pixel for performance)
    const sampleRate = Math.max(1, Math.floor((width * textAreaHeight) / 2000)); // Sample up to 2000 pixels
    
    for (let y = 0; y < textAreaHeight; y += sampleRate) {
      for (let x = 0; x < width; x += sampleRate) {
        const i = (y * width + x) * 4;
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        
        // Calculate brightness using relative luminance formula (WCAG)
        // This gives more weight to green and less to blue, matching human perception
        const brightness = (r * 0.299 + g * 0.587 + b * 0.114);
        
        totalBrightness += brightness;
        pixelCount++;
      }
    }
    
    const averageBrightness = pixelCount > 0 ? totalBrightness / pixelCount : 128;
    
    // Threshold: brightness > 128 is considered light, <= 128 is dark
    // We use a slightly lower threshold (120) to err on the side of white text
    // since white text on dark backgrounds is generally more readable
    const threshold = 120;
    const isLight = averageBrightness > threshold;
    const isDark = averageBrightness <= threshold;
    
    return {
      averageBrightness,
      isLight,
      isDark,
      useWhiteText: isDark,
      useBlackText: isLight,
    };
  } catch (error) {
    // Error during analysis - default to dark background (use white text)
    return {
      averageBrightness: 100,
      isLight: false,
      isDark: true,
      useWhiteText: true,
      useBlackText: false,
    };
  }
}
